// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * A visually engaging loading component for asset bridge reports.
 *
 * Displays a checklist of analysis steps that get "checked off" sequentially
 * while waiting for the API response. Provides users with feedback that
 * analysis work is happening rather than showing a simple spinner.
 *
 * Timing is dynamically calculated based on the organization's asset count
 * and the number of periods in the date range. Since bridge reports run
 * a full rollforward for each period, the timing scales with period count.
 *
 * Steps complete at timed intervals proportional to expected load time,
 * and accelerate to completion once actual data arrives.
 */
import {
  type BridgeReportType,
  ReportLoaderDisplay,
  bridgeReportConfigs,
  useReportLoaderProgress,
} from '@/report/components/report-loader';

interface Props {
  /** When true, accelerates remaining steps and completes quickly */
  isDataReady?: boolean;
  /** Called when all steps are complete */
  onComplete?: () => void;
  /** Type of bridge report being loaded (affects step labels and timing) */
  reportType: BridgeReportType;
  /** Number of periods in the date range (used for timing calculation) */
  periodCount: number;
}

/**
 * Displays a pseudo-progress loader with a checklist of analysis steps.
 * Fetches asset count to calculate realistic step timing, then animates
 * through steps proportionally. Timing scales with periodCount since
 * bridge reports compute a rollforward for each period. Accelerates
 * when actual data arrives.
 */
export function BridgeReportLoader(props: Props) {
  const { isDataReady, onComplete, reportType, periodCount } = props;

  const config = bridgeReportConfigs[reportType];

  // Bridge reports compute a rollforward per period, so timing scales
  // with periodCount (asset count x period count).
  const progress = useReportLoaderProgress({
    config,
    isDataReady,
    onComplete,
    periodCount,
    useAssetPeriods: true,
  });

  return (
    <ReportLoaderDisplay
      currentStep={progress.currentStep}
      completedSteps={progress.completedSteps}
      stepLabels={config.stepLabels}
    />
  );
}
